import React from "react";
import { NUSDIcon } from "../../../AssetComponents/Images";
import RangeSlider from "./RangeSlider";
import { NumberTicker } from "../NumberAnimation";
// Dynamic balance formatter: >= 1 shows 2 decimals, < 1 shows enough to see significant digits
const formatBalance = (val) => {
  if (val === null || val === undefined) return "0.00";
  const n = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(n) || n === 0) return "0.00";
  if (n >= 1) return n.toFixed(2);
  // For small values, find first significant digit
  let d = 2;
  while (d < 10 && Number(n.toFixed(d)) === 0) d++;
  return n.toFixed(Math.min(d + 2, 10));
};

// Updated helper to ensure numStr is a string
const formatWithComma = (numStr) => {
  const str = numStr.toString();
  if (str === "" || str === ".") return str;
  const parts = str.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

/**
 * Balance shares of the deposit.
 *
 * These replace the 0-100% rc-slider that used to sit under the input.
 * Sizing by balance share is a four-value decision in practice — a quarter, a
 * half, three quarters, everything — and the slider made the trader drag for a
 * value they were going to round anyway, then carried a 40px strip of tick
 * labels underneath to say what they had landed on. One tap, and the tapped
 * chip is the readout.
 */
const SHARES = [0, 25, 50, 75, 100];

const DepositAmount = ({
  amount,
  setAmount,
  tokenBalance,
  setAvailableAmount,
  onBridgeClick,
  onPercent,
  percent,
}) => {
  const balance = Number(tokenBalance) || 0;
  return (
    <div style={{ gap: 12 }} className="flex flex-col">
      <div className="flex justify-between items-center">
        <div style={{ fontFamily: "General Sans", fontSize: 12.5, fontWeight: 500, color: "var(--ink-2)" }}>Deposit Amount</div>
        <button
          type="button"
          onClick={() => balance > 0 && onPercent?.(100)}
          className="text-sm text-gray-500"
          style={{ background: "none", border: "none", padding: 0, cursor: balance > 0 ? "pointer" : "default" }}
          title={balance > 0 ? "Use full balance" : undefined}
        >
          Balance: {formatBalance(tokenBalance)} USDC
        </button>
      </div>

      <div
        className="ce-field flex items-center justify-between"
        /* `ce-field` carries no fill of its own here — the inline background and
           inset below already draw the well. It marks the *whole* row as the
           focus target, so the keyboard ring wraps the field including the unit
           pill instead of landing on the bare input inside it. */
        /* The field is the one thing on this panel you type into, so it is
           *recessed* — that is the whole raised/recessed contrast the system
           rests on. It used to be tinted in the market accent, which is a 2022
           signal: a coloured input says "this is the important one", a
           recessed one says "this is where the text goes". */
        style={{
          height: 52,
          borderRadius: 14,
          border: 0,
          paddingLeft: 16,
          paddingRight: 16,
          background: "var(--surface-2)",
          boxShadow: "var(--inset)",
        }}
      >
        <input
          type="text"
          value={amount ? formatWithComma(String(amount)) : amount}
          onChange={(e) => {
            const rawVal = e.target.value.replace(/,/g, "");

            if (rawVal === "") {
              setAmount("");
              setAvailableAmount("0");
              return;
            }

            if (rawVal === ".") {
              setAmount("0.");
              setAvailableAmount("0");
              return;
            }

            if (!/^\d*\.?\d*$/.test(rawVal)) return;

            const MAX_DEPOSIT_AMOUNT = 1000000000;
            const numericVal = parseFloat(rawVal);
            if (!isNaN(numericVal) && numericVal > MAX_DEPOSIT_AMOUNT) {
              setAmount(MAX_DEPOSIT_AMOUNT.toString());
              if (tokenBalance > 0) {
                const pct = (MAX_DEPOSIT_AMOUNT / tokenBalance) * 100;
                if (pct > 100) {
                  setAvailableAmount("100+");
                } else {
                  setAvailableAmount(pct.toFixed(2));
                }
              }
              return;
            }

            setAmount(rawVal);

            if (!isNaN(numericVal) && tokenBalance > 0) {
              const pct = (numericVal / tokenBalance) * 100;
              if (pct > 100) {
                setAvailableAmount("100+");
              } else {
                setAvailableAmount(pct.toFixed(2));
              }
            } else {
              setAvailableAmount("0");
            }
          }}
          onBlur={() => {
            const rawVal = String(amount).replace(/,/g, "");
            if (rawVal === "") {
              setAvailableAmount("0");
              return;
            }

            if (rawVal === ".") {
              setAmount("0");
              setAvailableAmount("0");
              return;
            }

            const parsed = parseFloat(rawVal);
            if (isNaN(parsed)) {
              setAmount("");
              setAvailableAmount("0");
            } else {
              const MAX_DEPOSIT_AMOUNT = 1000000000;
              const clampedAmount = Math.min(parsed, MAX_DEPOSIT_AMOUNT);

              setAmount(clampedAmount.toString());

              if (tokenBalance > 0) {
                const pct = (clampedAmount / tokenBalance) * 100;
                if (pct > 100) {
                  setAvailableAmount("100+");
                } else {
                  setAvailableAmount(pct.toFixed(2));
                }
              }
            }
          }}
          placeholder="0.00"
          className="bg-transparent outline-none w-full"
          style={{
            fontFamily: "General Sans",
            fontWeight: 700,
            fontSize: '26px',
            lineHeight: '135%',
            letterSpacing: '-.02em',
            fontVariantNumeric: 'tabular-nums',
            color: "var(--ink, #202020)",
          }}
        />

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* MAX has moved down into the share chips — it is the same decision
              as 25/50/75, and carrying it in two places on one control invited
              the question of whether the two did the same thing. */}
          {/* A raised pill against the recessed field — the contrast is what
              tells you which half is pressable. */}
          <div
            className="ce-pill flex items-center gap-1.5"
            onClick={onBridgeClick}
            style={{ padding: "5px 10px", cursor: onBridgeClick ? "pointer" : "default" }}
            title={onBridgeClick ? "Bridge USDC" : undefined}
          >
            <NUSDIcon className="w-4 h-4" />
            <span
              style={{
                fontFamily: "General Sans",
                fontWeight: 600,
                fontSize: 13,
                color: "var(--ink-2, #344054)",
              }}
            >
              USDC
            </span>
          </div>
        </div>
      </div>

      {/* Balance share slider + chips — disabled, not working correctly.
          Was: 5%-step slider + 0/25/50/75/MAX chips under "Balance used".
          The "Balance: X USDC" label above already sets 100% on click, and
          the amount field takes direct input, so this was redundant on top
          of being broken. Re-enable by flipping `false &&` back on once the
          underlying issue is fixed. */}
      {false && (
      <div style={{ opacity: balance > 0 ? 1 : 0.5, pointerEvents: balance > 0 ? "auto" : "none" }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 2 }}>
          <span style={{ fontFamily: "General Sans", fontSize: 12.5, color: "var(--ink-2, #667085)" }}>
            Balance used
          </span>
          <span style={{ fontFamily: "General Sans", fontSize: 12.5, fontWeight: 600, color: "var(--cat-accent, #5B1DEE)", fontVariantNumeric: "tabular-nums" }}>
            <NumberTicker text={`${Math.round(Number(percent) || 0)}%`} duration={0.4} stagger={0.02} />
          </span>
        </div>

        <RangeSlider
          value={Math.min(100, Math.round(Number(percent) || 0))}
          onChange={(v) => onPercent?.(v)}
          step={5}
          tickEvery={5}
          disabled={balance <= 0}
          ariaLabel="Share of balance to deposit"
          format={(v) => `${v}% of balance`}
        />

        <div className="flex items-center" style={{ marginTop: 4, gap: 4 }}>
          {SHARES.map((share) => {
            const active = Math.round(Number(percent) || 0) === share;
            return (
              <button
                key={share}
                type="button"
                disabled={balance <= 0}
                onClick={() => onPercent?.(share)}
                style={{
                  flex: "1 1 0",
                  height: 28,
                  borderRadius: 999,
                  background: active ? "var(--raise)" : "transparent",
                  boxShadow: active ? "var(--pillow-sm)" : "none",
                  border: "none",
                  cursor: balance > 0 ? "pointer" : "not-allowed",
                  fontFamily: "General Sans",
                  fontSize: 11.5,
                  fontWeight: active ? 700 : 500,
                  color: active ? "var(--cat-accent, #5B1DEE)" : "var(--ink-3)",
                  transition: "color .14s, box-shadow var(--dur-base) var(--ease)",
                }}
              >
                {share === 100 ? "MAX" : `${share}%`}
              </button>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
};

export default DepositAmount;
